import { useEffect } from 'react';
import { usePostHog } from '@posthog/react';
import { useAuth } from './AuthContext';
import { setToken, getEmail } from './token';
import { loadGoogleIdentity, createNonce, googleClientId, exchangeGoogleCredential } from './google';
import { useIsMobile } from '../utils/useMediaQuery';

// Google's One Tap prompt: the "Sign in to Gradies with Google / Continue"
// sheet that slides up from the bottom of the screen on phones. Offered to
// signed-out mobile visitors, never while the sign-in dialog is open (it's
// offered again after the dialog closes). Google decides the final look and
// throttles it itself: after
// someone dismisses it, Google waits longer and longer before showing it again.
const GoogleOneTap = () => {
    const { isLoggedIn, authModalMode, handleLoginSuccess, openLogin } = useAuth();
    const isMobile = useIsMobile();
    const posthog = usePostHog();

    const eligible = isMobile && !isLoggedIn && !authModalMode && !!googleClientId();

    useEffect(() => {
        if (!eligible) return undefined;
        let cancelled = false;
        let googleId = null;

        const show = async () => {
            try {
                const [gid, nonce] = await Promise.all([loadGoogleIdentity(), createNonce()]);
                if (cancelled) return;
                googleId = gid;
                googleId.initialize({
                    client_id: googleClientId(),
                    nonce: nonce.hashed,
                    context: 'signin',
                    auto_select: false,
                    cancel_on_tap_outside: true,
                    itp_support: true, // Safari/iOS: show the prompt despite tracking prevention
                    use_fedcm_for_prompt: true, // Chrome's browser-native version of the prompt
                    callback: async ({ credential }) => {
                        try {
                            setToken(await exchangeGoogleCredential(credential, nonce.raw));
                            const email = getEmail();
                            try {
                                posthog.identify(email, { email });
                                posthog.capture('user_logged_in', { email, method: 'google_one_tap' });
                            } catch { /* posthog optional */ }
                            handleLoginSuccess();
                        } catch (err) {
                            try { posthog.capture('user_login_failed', { reason: err.message, method: 'google_one_tap' }); } catch { /* ignore */ }
                            // Fall back to the dialog so they can try again and see what went wrong.
                            openLogin();
                        }
                    },
                });
                googleId.prompt();
            } catch { /* Google script didn't load; the Log In button still works */ }
        };

        show();
        return () => {
            cancelled = true;
            googleId?.cancel();
        };
    }, [eligible]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};

export default GoogleOneTap;
